import { zodResolver } from '@hookform/resolvers/zod'
import { FlaskConical, Link2, TimerReset } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormSection } from '@/components/common/FormBits'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { useConvertTrial, useExtendTrial, useOrders, useStartTrial } from '../../api'
import type { Trial } from '../../types'
import { toastError, useCrm } from '../../ui'
import { RoleAssignmentPicker } from '@/features/projects/rules/RoleAssignmentPicker'
import { Shell } from './RecordForms'

const DAY_OPTIONS = [7, 14, 21, 30, 45, 60]

/** Yeni deneme: modüller ve süre. Bitiş tarihi sunucuda hesaplanır. */
export function StartTrialForm({ customerId, onDone }: { customerId: string; onDone: () => void }) {
  const { t } = useTranslation()
  const start = useStartTrial()
  const { moduleCatalog } = useCrm()
  // Demo kurulum kuralları: her görev rolü için seçilen personel o göreve atanır.
  const [assignments, setAssignments] = useState<Record<string, string>>({})
  const schema = z.object({
    modules: z.array(z.string()).min(1, t('crm.trial.modulesRequired')),
    days: z.coerce.number<string | number>().int().min(1).max(60),
    notes: z.string().max(300),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { modules: ['Temel Yönetim'], days: '14', notes: '' } })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await start.mutateAsync({ ...v, customerId, assignments })
      toast.success(t('crm.trial.started'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell icon={<FlaskConical />} title={t('crm.trial.createTitle')} hint={t('crm.trial.hint')} pending={start.isPending} onDone={onDone} onSubmit={(e) => void onSubmit(e)} submitLabel={t('crm.trial.start')}>
      <FormSection>
        <Field id="tr-modules" label={t('crm.trial.modules')} error={errors.modules?.message} full>
          <Controller
            control={form.control}
            name="modules"
            render={({ field }) => (
              <div id="tr-modules" role="group" className="flex flex-wrap gap-2">
                {moduleCatalog.map((m) => {
                  const on = field.value.includes(m)
                  return (
                    <button
                      key={m}
                      type="button"
                      aria-pressed={on}
                      onClick={() => field.onChange(on ? field.value.filter((x) => x !== m) : [...field.value, m])}
                      className={cn('rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', on ? 'border-primary bg-primary/10 font-medium text-primary' : 'hover:bg-muted')}
                    >
                      {m}
                    </button>
                  )
                })}
              </div>
            )}
          />
        </Field>
        <Field id="tr-days" label={t('crm.trial.days')}>
          <FormSelect id="tr-days" control={form.control} name="days" options={DAY_OPTIONS.map((d) => ({ value: String(d), label: t('crm.trial.dayCount', { count: d }) }))} />
        </Field>
        <Field id="tr-notes" label={t('crm.fields.note')} full>
          <Textarea id="tr-notes" rows={2} maxLength={300} {...form.register('notes')} />
        </Field>
        <div className="sm:col-span-2">
          <RoleAssignmentPicker event="demo.created" accountId={customerId} value={assignments} onChange={setAssignments} />
        </div>
      </FormSection>
    </Shell>
  )
}

export function ExtendTrialForm({ trial, onDone }: { trial: Trial; onDone: () => void }) {
  const { t } = useTranslation()
  const extend = useExtendTrial()
  const schema = z.object({ days: z.coerce.number<string | number>().int().min(1).max(30), reason: z.string().trim().min(3, t('crm.trial.reasonRequired')).max(300) })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { days: '7', reason: '' } })
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await extend.mutateAsync({ id: trial.id, ...v })
      toast.success(t('crm.trial.extended', { count: v.days }))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell icon={<TimerReset />} title={t('crm.trial.extendTitle')} hint={t('crm.trial.extendHint', { date: fmtDate(trial.endsOn) })} pending={extend.isPending} onDone={onDone} onSubmit={(e) => void onSubmit(e)} submitLabel={t('crm.trial.extend')}>
      <FormSection>
        <Field id="te-days" label={t('crm.trial.extendDays')} full>
          <FormSelect id="te-days" control={form.control} name="days" options={[7, 14, 21, 30].map((d) => ({ value: String(d), label: t('crm.trial.dayCount', { count: d }) }))} />
        </Field>
        <Field id="te-reason" label={t('crm.trial.reason')} error={form.formState.errors.reason?.message} full>
          <Textarea id="te-reason" rows={2} maxLength={300} {...form.register('reason')} />
        </Field>
      </FormSection>
    </Shell>
  )
}

/** Denemeyi müşterinin siparişine bağlar; lisans o siparişin onayıyla açılır. */
export function ConvertTrialForm({ trial, onDone }: { trial: Trial; onDone: () => void }) {
  const { t } = useTranslation()
  const convert = useConvertTrial()
  const orders = (useOrders(trial.customerId).data ?? []).filter((o) => o.status !== 'rejected')
  const schema = z.object({ orderId: z.string().min(1, t('crm.trial.orderRequired')) })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { orderId: orders[0]?.id ?? '' } })
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await convert.mutateAsync({ id: trial.id, orderId: v.orderId })
      toast.success(t('crm.trial.converted'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell icon={<Link2 />} title={t('crm.trial.convertTitle')} hint={t('crm.trial.convertHint')} pending={convert.isPending} onDone={onDone} onSubmit={(e) => void onSubmit(e)} submitLabel={t('crm.trial.convert')}>
      <FormSection>
        <Field id="tc-order" label={t('crm.trial.order')} error={form.formState.errors.orderId?.message} hint={orders.length ? undefined : t('crm.trial.noOrders')} full>
          <FormSelect
            id="tc-order"
            control={form.control}
            name="orderId"
            placeholder={t('crm.trial.pickOrder')}
            options={orders.map((o) => ({ value: o.id, label: `${o.number} · ${fmtMoney(o.total)}`, description: t(`crm.orderStatus.${o.status}`) }))}
          />
        </Field>
      </FormSection>
    </Shell>
  )
}
